import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import { STATUS_LABEL, fetchInvoice, payInvoice } from '@/lib/cards';
import { fmtBRL, fmtDate, listAccounts } from '@/lib/finance';

export function InvoicePage() {
  const { invoiceId = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const invoiceQ = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => fetchInvoice(invoiceId),
  });
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [payOpen, setPayOpen] = useState(false);

  if (invoiceQ.isLoading) return <p className="text-slate-500">carregando…</p>;
  if (invoiceQ.error)
    return <p className="text-red-600">{(invoiceQ.error as Error).message}</p>;
  if (!invoiceQ.data) return null;

  const inv = invoiceQ.data;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-xs text-slate-500 hover:underline"
          >
            ← voltar
          </button>
          <h1 className="text-xl font-semibold">Fatura {inv.competence}</h1>
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
            inv.status === 'paid'
              ? 'bg-emerald-50 text-emerald-700'
              : inv.status === 'overdue'
                ? 'bg-red-50 text-red-700'
                : inv.status === 'closed'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-100 text-slate-600'
          }`}
        >
          {STATUS_LABEL[inv.status]}
        </span>
      </header>

      <section className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-4">
        <Info label="total" value={fmtBRL(inv.total_amount)} />
        <Info label="fechamento" value={fmtDate(inv.closing_date)} />
        <Info label="vencimento" value={fmtDate(inv.due_date)} />
        <Info label="lançamentos" value={String(inv.transactions.length)} />
      </section>

      {inv.status !== 'paid' && parseFloat(inv.total_amount) > 0 && (
        <button
          onClick={() => setPayOpen(true)}
          className="tap rounded-md bg-slate-900 px-4 py-3 font-medium text-white"
        >
          pagar fatura
        </button>
      )}

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <h2 className="border-b border-slate-100 px-4 py-2 text-sm font-medium uppercase tracking-wide text-slate-500">
          lançamentos
        </h2>
        {inv.transactions.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">nenhum lançamento.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {inv.transactions.map((t) => (
              <li
                key={t.id}
                className="flex items-center justify-between gap-3 px-4 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate">{t.description}</p>
                  <p className="text-[11px] text-slate-500">{fmtDate(t.effective_date)}</p>
                </div>
                <span
                  className={`shrink-0 font-medium ${
                    t.direction === 'out' ? 'text-red-600' : 'text-emerald-700'
                  }`}
                >
                  {t.direction === 'out' ? '−' : '+'} {fmtBRL(t.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <PayModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        invoiceId={inv.id}
        totalAmount={inv.total_amount}
        accounts={accountsQ.data?.filter((a) => a.type !== 'credit_card') ?? []}
        onPaid={() => {
          setPayOpen(false);
          toast.success('fatura paga');
          qc.invalidateQueries({ queryKey: ['invoice', invoiceId] });
          qc.invalidateQueries({ queryKey: ['cards'] });
          qc.invalidateQueries({ queryKey: ['accounts'] });
          qc.invalidateQueries({ queryKey: ['transactions'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}

function PayModal({
  open,
  onClose,
  invoiceId,
  totalAmount,
  accounts,
  onPaid,
}: {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  totalAmount: string;
  accounts: { id: string; name: string }[];
  onPaid: () => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState(totalAmount);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      payInvoice(invoiceId, {
        from_account_id: accountId,
        amount,
        effective_date: date,
      }),
    onSuccess: () => onPaid(),
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accountId) {
      setError('escolha a conta de origem.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal open={open} title="Pagar fatura" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Conta de origem</span>
          <select
            required
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="">— selecionar —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Valor (R$)</span>
          <input
            required
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Data do pagamento</span>
          <input
            required
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        {error && <p className="text-red-600">{error}</p>}
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'pagando…' : 'pagar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
